import { useEffect, useMemo, useRef } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { useDisclosure } from "@chakra-ui/react";
import {
	NewOrderEarprintShippingType,
	NewOrderType,
	useNewOrderStore,
} from "../../../stores/order.store";
import { CommandStatus } from "../../../types/command.types";
import { useCenterUsersPaginated } from "../../../hooks/centers.hooks";
import { useConnectedUser } from "../../../hooks/user.hooks";
import { useSimpleToast } from "../../../hooks/toast.hooks";
import LeftEarCard from "../../../components/LeftEarCard";
import RightEarCard from "../../../components/RightEarCard";
import DetailCard from "../../../components/DetailCard";
import PatientCard from "../../../components/PatientCard";
import CenterCard from "../../../components/CenterCard";
import DateCard from "../../../components/DateCard";
import SimpleModal from "../../../components/modals/SimpleModal";
import { SelectUserCircle } from "./SelectUserCircle";
import { OrdersNewPageBaseLayout } from "./OrdersNewPageBaseLayout";
import { addBusinessDays } from "date-fns";
import AudiosCard from "../../../components/AudiosCard";
import BrandAlertIcon from "../../../components/icons/BrandAlertIcon";
import { OrdersNewPageBaseLayoutValidate } from "./OrdersNewPageBaseLayoutValidate";
import { useAdminSettings } from "../../../hooks/admin_settings.hooks";
import { getEstimatedProductionDays } from "./getEstimatedProductionDays";

export const ValidationLayout = () => {
	const {
		earmold,
		leftEarForm,
		rightEarForm,
		commentary,
		patient,
		center,
		additionalFiles,
		hasLeftEar,
		hasRightEar,
		saveDraft,
		audioResponsible,
		isPriority,
		earprintShippingType,
		type,
	} = useNewOrderStore();
	const user = useConnectedUser();
	const toast = useSimpleToast();
	const isValidating = useRef(false);
	const sharedPostMutation = {
		onSuccess: async () => {
			toast.success("Commande", "La commande est validée");
		},
		onError: () => {
			toast.error("Commande", "La commande n'a pas pu être validée");
		},
	};
	const mutateValidate = useMutation(saveDraft, sharedPostMutation);
	const mutateValidateAndSee = useMutation(saveDraft, sharedPostMutation);
	const creatorSelectionModal = useDisclosure();
	const navigate = useNavigate();
	const usersPaginated = useCenterUsersPaginated({ id: center?.id })(1, 1000);
	const { creator, setCreator, updating } = useNewOrderStore();
	const queryClient = useQueryClient();
	const { data: adminSettings } = useAdminSettings();
	const estimateTheProductionDays = useMemo(() => {
		return getEstimatedProductionDays({
			adminSettings: adminSettings ?? undefined,
			isPhysical:
				earprintShippingType === NewOrderEarprintShippingType.PHYSICAL,
			isPriority: isPriority === true,
			isRework: type === NewOrderType.REWORK,
		});
	}, [adminSettings, earprintShippingType, isPriority, type]);

	useEffect(() => {
		if (creator !== undefined) return;

		creatorSelectionModal.onOpen();
	}, [creator, creatorSelectionModal]);

	return (
		<OrdersNewPageBaseLayout>
			<Flex direction="column" gap={12} alignItems="center">
				<Flex direction="row" gap={4}>
					<Flex direction="column" gap={4} w="max(600px, 46vw)">
						<Flex direction="row" gap={4}>
							{hasRightEar && (
								<Flex w="max(300px,23vw)">
									<RightEarCard earmold={earmold} form={rightEarForm} />
								</Flex>
							)}
							{hasLeftEar && (
								<Flex w="max(300px,23vw)">
									<LeftEarCard earmold={earmold} form={leftEarForm} />
								</Flex>
							)}
						</Flex>
						<Flex>
							<DetailCard
								comment={commentary}
								documents={[...(additionalFiles ?? [])].filter(
									(e) => e !== undefined,
								)}
							/>
						</Flex>
					</Flex>
					<Flex direction="column" w="max(300px,23vw)" gap={4}>
						{isPriority && (
							<div className="flex items-center w-fit px-2 gap-2 rounded-xl bg-error-50 border! border-error-300!">
								<BrandAlertIcon className="text-error-600! w-3 h-3" />
								<Text
									fontSize="16px"
									fontWeight={600}
									className="text-error-700"
								>
									Commande prioritaire
								</Text>
							</div>
						)}
						<DateCard
							showReceiptTime={
								earprintShippingType ===
									NewOrderEarprintShippingType.PHYSICAL &&
								type !== NewOrderType.REWORK
							}
							date={addBusinessDays(new Date(), estimateTheProductionDays)}
						/>
						<PatientCard patient={patient} />
						<CenterCard center={center} />
						<AudiosCard
							author={user ?? undefined}
							responsible={audioResponsible ?? undefined}
						/>
					</Flex>
				</Flex>

				<div className="h-20"></div>
				<OrdersNewPageBaseLayoutValidate
					onValidatePending={mutateValidate.isLoading}
					onValidateAndSeePending={mutateValidateAndSee.isLoading}
					onValidateAndSee={() => {
						if (isValidating.current === false) {
							isValidating.current = true;
							if (updating) {
								mutateValidateAndSee.mutate(
									{
										status: CommandStatus.WAITING_FOR_RECEPTION,
										log: true,
									},
									{
										onSuccess: async (command) => {
											await queryClient.invalidateQueries();
											if (command?.id) {
												window
													.open(
														`/orders/${command.id}/document/preview`,
														"_blank",
													)
													?.focus();
											}
											navigate("/orders");
										},
									},
								);
							} else {
								mutateValidateAndSee.mutate(
									{
										status: CommandStatus.WAITING_FOR_RECEPTION,
									},
									{
										onSuccess: async (command) => {
											await queryClient.invalidateQueries();
											if (command?.id) {
												window
													.open(
														`/orders/${command.id}/document/preview`,
														"_blank",
													)
													?.focus();
											}
											navigate("/orders");
										},
									},
								);
							}
						}
					}}
					onValidate={() => {
						if (isValidating.current === false) {
							isValidating.current = true;
							if (updating) {
								mutateValidate.mutate(
									{
										status: CommandStatus.WAITING_FOR_RECEPTION,
										log: true,
									},
									{
										onSuccess: async () => {
											await queryClient.invalidateQueries();
											navigate("/orders");
											toast.success("", "Commande prise en compte");
										},
									},
								);
							} else {
								mutateValidate.mutate(
									{
										status: CommandStatus.WAITING_FOR_RECEPTION,
									},
									{
										onSuccess: async () => {
											await queryClient.invalidateQueries();
											navigate("/orders");
										},
									},
								);
							}
						}
					}}
				/>

				<SimpleModal
					isOpen={creatorSelectionModal.isOpen}
					onClose={creatorSelectionModal.onClose}
					header={false}
					w={"700px"}
					abortText="Retour"
					acceptText="Valider"
					hideCloseButton
					hideAbortButton
					isPending={mutateValidate.isLoading}
					onClick={() => {
						creatorSelectionModal.onClose();
					}}
				>
					<Flex direction="column">
						<Text
							mb={8}
							textAlign={"center"}
							lineHeight="36px"
							fontSize="24px"
							fontWeight="600"
						>
							Qui saisit la commande ?
						</Text>

						<Box w="100%">
							<SelectUserCircle
								users={usersPaginated ? usersPaginated.data : []}
								userSelected={creator}
								setUserSelected={setCreator}
								role
								centerId={center?.id}
							/>
						</Box>
					</Flex>
				</SimpleModal>
			</Flex>
		</OrdersNewPageBaseLayout>
	);
};
